import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  User,
  Loader2,
  XCircle,
  Mail,
  X,
  Send,
  CheckCircle2,
  Lock,
} from "lucide-react";

// Başarılı işlem bildirimi için kullanılan modal bileşeni
const SuccessModal = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl p-8 max-w-sm w-full shadow-2xl text-center relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors"
        >
          <X size={24} />
        </button>
        <CheckCircle2 size={64} className="text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-50 mb-2">Başarılı!</h2>
        <p className="text-gray-300">{message}</p>
        <button
          onClick={onClose}
          className="mt-6 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors"
        >
          Kapat
        </button>
      </div>
    </div>
  );
};

// E-posta gönderim formu için kullanılan modal bileşeni
const EmailModal = ({ selectedUser, onClose, onSendEmail, isSendingEmail }) => {
  const [emailSubject, setEmailSubject] = useState("Hesap Onayı Hakkında");
  const [emailBody, setEmailBody] = useState(
    `Merhaba ${selectedUser.username},\n\nMekan Name platformundaki hesabınızın onaylanması için gerekli bilgileri tamamlamanızı rica ediyoruz.\n\nSaygılarımızla,\nmekanname.com Ekibi`
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSendEmail(emailSubject, emailBody);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors"
        >
          <X size={24} />
        </button>
        <h2 className="text-2xl font-bold text-gray-50 mb-4">
          Kullanıcıya E-posta Gönder
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Alıcı
            </label>
            <input
              type="text"
              value={`${selectedUser.username} <${selectedUser.email}>`}
              readOnly
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-300 focus:outline-none"
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="email-subject"
              className="block text-sm font-medium text-gray-400 mb-1"
            >
              Konu
            </label>
            <input
              id="email-subject"
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="email-body"
              className="block text-sm font-medium text-gray-400 mb-1"
            >
              İçerik
            </label>
            <textarea
              id="email-body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows="6"
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            ></textarea>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-gray-200 rounded-lg hover:bg-gray-500 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSendingEmail}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                isSendingEmail
                  ? "bg-indigo-400 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-500"
              }`}
            >
              {isSendingEmail ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Gönder
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Kullanıcı listesini API'den çeken fonksiyon
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/users`);
        setUsers(res.data);
      } catch (err) {
        setError("Kullanıcılar alınamadı. Lütfen tekrar deneyin.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // E-posta modalını açan fonksiyon
  const openEmailModal = (user) => {
    setSelectedUser(user);
    setIsEmailModalOpen(true);
  };

  // E-posta modalını kapatan fonksiyon
  const closeEmailModal = () => {
    setIsEmailModalOpen(false);
    setSelectedUser(null);
  };

  // E-posta gönderme işlemini backend'e ileten fonksiyon
  const sendEmail = async (subject, body) => {
    if (!selectedUser) {
      console.error("Kullanıcı seçili değil.");
      return;
    }
    if (!subject || !body) {
      // alert yerine modal kullanacağımız için bu kısım değişebilir.
      setSuccessMessage("Hata: Lütfen tüm alanları doldurun.");
      setIsSuccessModalOpen(true);
      return;
    }
    setIsSendingEmail(true);

    try {
      const payload = {
        to: selectedUser.email,
        subject: subject,
        body: body,
      };

      // E-posta gönderme API'sine POST isteği atıyoruz
      const response = await fetch(`${API_URL}/api/send_email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`E-posta gönderme hatası! Durum: ${response.status}`);
      }

      const result = await response.json();
      closeEmailModal();
      setSuccessMessage(
        result.message || "E-posta başarıyla gönderildi."
      );
      setIsSuccessModalOpen(true);

    } catch (err) {
      console.error("İşlem hatası:", err);
      setSuccessMessage(`Hata: ${err.message}`);
      setIsSuccessModalOpen(true);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Bilinmiyor";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("tr-TR", options);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        <p className="ml-4 text-xl">Kullanıcılar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-red-400 p-6 text-center">
        <XCircle className="h-16 w-16 mb-4" />
        <p className="text-2xl font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gray-900 text-gray-50 min-h-screen font-sans">
      <div className="flex items-center gap-4 mb-8">
        <User className="h-12 w-12 text-blue-400" />
        <h1 className="text-4xl font-extrabold text-white">Kullanıcılar</h1>
      </div>
      <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-700">
              <tr className="text-left text-sm font-semibold text-gray-300">
                <th className="px-6 py-4">Ad</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Kayıt Tarihi</th>
                <th className="px-6 py-4">Onay</th>
                <th className="px-6 py-4 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-gray-500">
                    Kayıtlı kullanıcı bulunamadı.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-700 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 font-medium text-gray-300">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 text-gray-400">{user.email}</td>
                    <td className="px-6 py-4 text-gray-500">
                      {formatDate(user.registered_at)}
                    </td>
                    <td className="px-6 py-4">
                      {user.is_verified === true ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-900 text-green-300">
                          <CheckCircle2 size={12} className="mr-1" /> Onaylandı
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-900 text-red-300">
                          <XCircle size={12} className="mr-1" /> Onaylanmadı
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {user.is_verified !== 0 ? (
                        <button
                          onClick={() => openEmailModal(user)}
                          title="Onay için E-posta Gönder"
                          className="text-indigo-400 hover:text-indigo-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Mail size={30} />
                        </button>
                      ) : (
                        <button
                          title="Hesap Zaten Onaylandı"
                          className="text-gray-500 cursor-not-allowed"
                          disabled
                        >
                          <Lock size={30} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* E-posta Gönderim Modalı */}
      {isEmailModalOpen && selectedUser && (
        <EmailModal
          selectedUser={selectedUser}
          onClose={closeEmailModal}
          onSendEmail={sendEmail}
          isSendingEmail={isSendingEmail}
        />
      )}
      {/* Başarılı İşlem Modalı */}
      {isSuccessModalOpen && (
        <SuccessModal
          message={successMessage}
          onClose={() => setIsSuccessModalOpen(false)}
        />
      )}
    </div>
  );
}
